import type { MutableRefObject, ReactElement } from 'react';

import { Box, Flex, Text } from '@rango-dev/whale';
import { Pagination } from '@rango-dev/whale/lab';
import { ArrowDownUpIcon, CircleStarIcon } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { usePaginationLabels } from '../../../hooks/usePaginationLabels';
import { useWindowDimensions } from '../../../hooks/useWindowDimensions';
import { capitalizeFirstLetter } from '../../../utils/generalFunctions/common';
import { HoverCard } from '../../Profile/HoverCard';
import { List } from '../../Profile/Leaderboard/List';

import styles from './Leaderboard.module.css';
import { CampaignType, type PropTypes } from './Leaderboard.types';

export function Leaderboard(props: PropTypes): ReactElement | null {
  const { t } = useTranslation();
  const {
    campaignId,
    type = CampaignType.TRANSACTION,
    tooltip,
    userData,
    leaderboardData,
    leaderboardLoading,
    leaderboardTotalPages,
    handlePageNavigation,
    page,
  } = props;

  const { isMobile } = useWindowDimensions();
  const isWideScreen = useMediaQuery('(min-width: 1280px)');
  const paginationLabels = usePaginationLabels();
  const previousPageRef: MutableRefObject<number | null> = useRef(null);

  const targetRef = useRef<HTMLDivElement | null>(null);

  const isEmptyLeaderboard = !leaderboardData || leaderboardData.length === 0;

  const scrollToTarget = () => {
    targetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /*
   * Above 1280px gets the full Set with the jump-to-page field; at or below
   * 1280px the compact Root (nav controls only), centered, keeps the footer tidy.
   */
  const renderPagination = (): ReactElement => {
    if (!leaderboardTotalPages) {
      return <Pagination.Skeleton />;
    }
    if (isWideScreen) {
      return (
        <Pagination.Set
          scrollTargetRef={targetRef}
          showEdgePages={!isMobile}
          currentPage={page}
          totalPages={leaderboardTotalPages}
          onPageChange={handlePageNavigation}
          labels={paginationLabels}
        />
      );
    }
    return (
      <Flex justify="center" width="100%">
        <Pagination.Root
          showEdgePages={!isMobile}
          currentPage={page}
          totalPages={leaderboardTotalPages}
          onPageChange={handlePageNavigation}
          labels={paginationLabels}
        />
      </Flex>
    );
  };

  useEffect(() => {
    if (previousPageRef.current !== null && previousPageRef.current !== page) {
      scrollToTarget();
    }
    previousPageRef.current = page;
  }, [page]);

  if (isEmptyLeaderboard && !leaderboardLoading) {
    return null;
  }

  const title = type === CampaignType.VOLUME ? t('Total Volume') : t('Transactions');
  const description =
    tooltip ||
    (type === CampaignType.VOLUME
      ? t('Total volume from swaps of $100 or more via {{campaignName}}', {
          campaignName: capitalizeFirstLetter(campaignId),
        })
      : t(`Transactions completed during the campaign that meet the task criteria.`));
  const icon =
    type === CampaignType.VOLUME ? (
      <ArrowDownUpIcon color="var(--gray-a12)" size={20} />
    ) : (
      <CircleStarIcon color="var(--gray-a12)" size={20} />
    );

  return (
    <Box
      ref={targetRef}
      width="100%"
      overflow="hidden"
      maxHeight={{ initial: '1646px', md: '1576px' }}
      className={styles.listWrapper}>
      <Flex justify="between" align="center" width="100%" px="2" pb="3">
        <Flex align="center" gap={{ initial: '2', sm: '4' }}>
          <Flex width="48px" align="start">
            <Text size="2" weight="bold">
              {t('Rank')}
            </Text>
          </Flex>
          <Text size="2" weight="bold">
            {t('User')}
          </Text>
        </Flex>
        <Flex align="center" gap="1">
          <Text size="2" weight="bold">
            {title}
          </Text>
          <HoverCard iconSize={15} description={description} />
        </Flex>
      </Flex>
      <List
        showLevel={false}
        avatarSize={{ initial: '3', md: '4' }}
        {...(!leaderboardData || leaderboardLoading
          ? {
              loading: true,
              maxLengthSkeleton: 20,
              enableTopUserSkeleton: page === 0,
            }
          : {
              loading: false,
              enableTopUserStyles: true,
              list: leaderboardData,
              userId: userData?.id,
              showCurrentUserTag: true,
              itemClassName: 'leaderboard-item',
              scoreIcon: icon,
            })}
      />

      <Box height="20px" />
      {renderPagination()}

      <Box height="36px" />
    </Box>
  );
}
